import { useEffect, useMemo, useState } from "react";
import type { Path } from "@/router";
import { useForm } from "react-hook-form";
import MultiStepForm from "@/components/MultiStepForm";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import EmailPreviewDialog, {
  EMAIL_PREVIEW_MODAL_CLASSNAME,
  EmailPreviewModalActions
} from "@/components/EmailPreviewDialog";
import { createEventSchema, FormValues } from "../_form/validation/schema";
import EventDetailsStep from "../_form/steps/EventDetailsStep";
import SessionDetailsStep from "../_form/steps/SessionDetailsStep";
import RegistrationDetailsStep from "../_form/steps/RegistrationDetailsStep";
import PricingStep from "../_form/steps/PricingStep";
import CustomFieldsStep from "../_form/steps/CustomFieldsStep";
import EventDescriptionStep from "../_form/steps/EventDescriptionStep";
import { toDateFormat } from "@/lib/dateHelpers";
import { getDefaultFormValues, getEarliestSession, isSeminarFormEvent, toSessionDateTime } from "./utils";
import { getSessionPreviewCandidates } from "./sessionPreviewInput";
import { useEventSessionUpdatedEmailPreview } from "../_hooks/useEventSessionUpdatedEmailPreview";
import { useRecruitmentEventCreationEmailPreview } from "../_hooks/useRecruitmentEventCreationEmailPreview";
import Select from "@/components/Select";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import ModulesStep from "../_form/steps/ModulesStep";
import classNames from "classnames";
import { useEventsBasePath } from "../_eventsBasePathContext";
import {
  StepVisibilityProvider,
  StepSlugProvider,
  EventDefaultsProvider,
  type StepVisibilityMap
} from "./FormVisibilityContext";

interface EventFormProps {
  initialValues?: Partial<FormValues>;
  initialStepVisibility?: StepVisibilityMap;
  onSubmit: (data: FormValues) => Promise<void>;
  isEditing?: boolean;
  hasRegistrations?: boolean;
  hasWaitlistEntries?: boolean;
  isSubmitting?: boolean;
  backLinkTo?: string;
  eventId?: string;
}

export default function EventForm({
  initialValues,
  initialStepVisibility,
  onSubmit,
  isEditing = false,
  hasRegistrations = false,
  hasWaitlistEntries = false,
  isSubmitting = false,
  backLinkTo,
  eventId
}: EventFormProps) {
  const basePath = useEventsBasePath();
  const originalRegistrationCloseDate = initialValues?.registrationCloseDate;
  const eventSchema = createEventSchema(isEditing, originalRegistrationCloseDate);
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false);
  const [showSaveAsDraftDialog, setShowSaveAsDraftDialog] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedSessionPreviewId, setSelectedSessionPreviewId] = useState<string | null>(null);
  const {
    loadPreview: loadSessionPreview,
    resetPreview: resetSessionPreview,
    preview: sessionPreview,
    loading: sessionPreviewLoading,
    error: sessionPreviewError
  } = useEventSessionUpdatedEmailPreview();
  const {
    loadPreview: loadRecruitmentCreatePreview,
    resetPreview: resetRecruitmentCreatePreview,
    preview: recruitmentCreatePreview,
    loading: recruitmentCreatePreviewLoading,
    error: recruitmentCreatePreviewError
  } = useRecruitmentEventCreationEmailPreview();

  const form = useForm<FormValues>({
    defaultValues: {
      ...getDefaultFormValues(),
      ...initialValues
    },
    resolver: zodResolver(eventSchema)
  });

  // Wrap onSubmit to ensure draft is false for publish action
  const handlePublish = async (data: FormValues) => {
    await onSubmit({ ...data, draft: false });
  };

  // Show save as draft dialog after validation
  const handleShowSaveAsDraftDialog = async () => {
    if (isSavingAsDraft || isSubmitting) return;

    const isValid = await form.trigger();
    if (isValid) {
      setShowSaveAsDraftDialog(true);
    }
  };

  // Confirm save as draft action
  const handleConfirmSaveAsDraft = async () => {
    setIsSavingAsDraft(true);
    try {
      const data = { ...form.getValues(), draft: true };
      await onSubmit(data);
      if (!isSavingAsDraft) {
        setShowSaveAsDraftDialog(false);
      }
    } catch (error) {
      console.error("Save as draft failed:", error);
    } finally {
      setIsSavingAsDraft(false);
    }
  };

  const { getFieldState, watch, setValue } = form;

  const sessions = watch("sessions");
  const registrationType = watch("registrationType");
  const globalMaxAttendance = watch("globalMaxAttendance");
  const timezone = watch("timezone");
  const registrationOpenDate = watch("registrationOpenDate.startDate");
  const registrationCloseDate = watch("registrationCloseDate.startDate");
  const eventName = watch("eventName");
  const hasMaxAttendance =
    registrationType === "all" && sessions.length > 1
      ? !!globalMaxAttendance
      : sessions.some(({ maxAttendance }) => !!maxAttendance);

  useEffect(() => {
    if (!hasMaxAttendance) {
      setValue("waitlistType", "disabled");
    }
  }, [hasMaxAttendance, setValue]);

  const eventType = watch("eventType");
  const orderTemplateId = watch("orderTemplateId");
  const eventFees = watch("eventFees");
  const isRecruitment = eventType.name === "Recruitment";
  const isSeminarPricing = isSeminarFormEvent({
    eventType,
    orderTemplateId,
    eventFees
  });
  const [stepVisibility, setStepVisibility] = useState<StepVisibilityMap | undefined>(initialStepVisibility);

  useEffect(() => {
    setStepVisibility(initialStepVisibility);
  }, [initialStepVisibility]);

  const isStepHidden = (slug: string) => stepVisibility?.[slug] === "hidden";

  const buildSteps = <T extends { slug: string; headline: string; content: React.ReactNode }>(items: T[]) =>
    items
      .filter((step) => !isStepHidden(step.slug))
      .map((step, i) => ({
        ...step,
        content: <StepSlugProvider slug={step.slug}>{step.content}</StepSlugProvider>,
        supportingText: `Step ${i + 1}`
      }));

  let steps = buildSteps([
    {
      slug: "eventDetails",
      headline: "Event Details",
      content: (
        <EventDetailsStep
          hasRegistrations={hasRegistrations}
          isEditing={isEditing}
          originalRegistrationCloseDate={originalRegistrationCloseDate}
        />
      ),
      requiredFields: [
        "eventName",
        "eventType",
        "section",
        "sectionId",
        "locationType",
        "facility",
        "facilityId",
        "address1",
        "city",
        "state",
        "zipcode"
      ]
    },
    {
      slug: "sessionDetails",
      headline: "Session Details",
      content: (
        <SessionDetailsStep isEditing={isEditing} originalRegistrationCloseDate={originalRegistrationCloseDate} />
      ),
      requiredFields: ["sessions"]
    },
    {
      slug: "registrationOptions",
      headline: "Registration Options",
      supportingText: "Step 3",
      content: (
        <RegistrationDetailsStep
          hasMaxAttendance={hasMaxAttendance}
          isEditing={isEditing}
          hasWaitlistEntries={hasWaitlistEntries}
        />
      ),
      requiredFields: ["registrationOpenDate", "registrationCloseDate"]
    },
    {
      slug: "pricing",
      headline: "Pricing",
      content: <PricingStep hasRegistrations={hasRegistrations} />,
      requiredFields: isSeminarPricing ? ["eventFees"] : ["products"]
    },
    {
      slug: "customFields",
      headline: "Additional Information",
      content: <CustomFieldsStep />,
      requiredFields: []
    },
    {
      slug: "modules",
      headline: "Modules",
      content: <ModulesStep />,
      requiredFields: []
    },
    {
      slug: "eventDescription",
      headline: "Event Description",
      content: <EventDescriptionStep />,
      requiredFields: []
    }
  ]);

  // For Recruitment events, reduce to 3 steps
  if (isRecruitment) {
    steps = buildSteps([
      {
        slug: "eventDetails",
        headline: "Event Details",
        content: (
          <EventDetailsStep
            hasRegistrations={hasRegistrations}
            isEditing={isEditing}
            originalRegistrationCloseDate={originalRegistrationCloseDate}
          />
        ),
        requiredFields: ["eventName", "eventType", "section", "sectionId", "locationType"]
      },
      {
        slug: "sessionDetails",
        headline: "Session Details",
        content: (
          <SessionDetailsStep isEditing={isEditing} originalRegistrationCloseDate={originalRegistrationCloseDate} />
        ),
        requiredFields: ["sessions"]
      },
      {
        slug: "modules",
        headline: "Modules",
        content: <ModulesStep />,
        requiredFields: []
      }
    ]);
  }

  const earliestSession = getEarliestSession(sessions, timezone);
  const notifySessionsChanged = isEditing && hasRegistrations && getFieldState("sessions").isDirty;
  const sessionPreviewCandidates = useMemo(
    () => getSessionPreviewCandidates(initialValues?.sessions, sessions, timezone),
    [initialValues?.sessions, sessions, timezone]
  );
  const resolvedSessionPreviewId = useMemo(() => {
    if (sessionPreviewCandidates.length === 0) {
      return null;
    }

    if (
      selectedSessionPreviewId &&
      sessionPreviewCandidates.some((candidate) => candidate.sessionId === selectedSessionPreviewId)
    ) {
      return selectedSessionPreviewId;
    }

    return sessionPreviewCandidates[0].sessionId;
  }, [sessionPreviewCandidates, selectedSessionPreviewId]);

  const sessionPreviewInput = useMemo(() => {
    if (!resolvedSessionPreviewId) {
      return null;
    }

    const selected = sessionPreviewCandidates.find((candidate) => candidate.sessionId === resolvedSessionPreviewId);

    if (!selected) {
      return null;
    }

    const { label: _label, ...input } = selected;
    return input;
  }, [sessionPreviewCandidates, resolvedSessionPreviewId]);
  const showRecruitmentCreatePreview = isRecruitment && !isEditing && Boolean(eventName?.trim());
  const showSessionUpdatePreview = notifySessionsChanged && Boolean(sessionPreviewInput);
  const showSubmitEmailPreview = showRecruitmentCreatePreview || showSessionUpdatePreview;
  const activePreview = sessionPreview ?? recruitmentCreatePreview;
  const activePreviewLoading = sessionPreviewLoading || recruitmentCreatePreviewLoading;
  const activePreviewError = sessionPreviewError ?? recruitmentCreatePreviewError;

  const resetSubmitPreview = () => {
    setPreviewOpen(false);
    resetSessionPreview();
    resetRecruitmentCreatePreview();
  };

  const handlePreviewSubmitEmail = () => {
    setPreviewOpen(true);

    if (showSessionUpdatePreview && sessionPreviewInput) {
      void loadSessionPreview(sessionPreviewInput);
      return;
    }

    if (showRecruitmentCreatePreview) {
      void loadRecruitmentCreatePreview({
        eventId: eventId ?? null,
        eventTitle: eventName
      });
    }
  };

  const submitDialogText = (isEditing: boolean, isRecruitment: boolean, notifySessionsChanged: boolean) => {
    let text = isRecruitment
      ? isEditing
        ? "Are you sure you want to submit changes to this recruitment event?"
        : "Are you sure you want to submit this event? PGA Recruiting will also be notified about the event."
      : isEditing
        ? "Are you sure you want to publish changes to this event?"
        : "Are you sure you want to publish this event and make it visible to potential registrants?";

    if (notifySessionsChanged) {
      text += " Because you have made changes to session dates/times, recipients will be notified.";
    }

    return text;
  };

  const submitDialogContent = (
    <div className="flex flex-col">
      <p className="text-body-medium pb-6">{submitDialogText(isEditing, isRecruitment, notifySessionsChanged)}</p>
      {showSessionUpdatePreview && sessionPreviewCandidates.length > 1 && (
        <Select
          className="pb-6"
          label="Preview email for session"
          value={resolvedSessionPreviewId ?? ""}
          options={sessionPreviewCandidates.map((candidate) => ({
            value: candidate.sessionId,
            displayText: candidate.label
          }))}
          onSelect={(value) => setSelectedSessionPreviewId(value)}
        />
      )}
      <p className="text-body-medium">
        <span className="font-medium">Start Date:</span>{" "}
        {toSessionDateTime(earliestSession?.date?.startDate, earliestSession?.startTime, timezone)}
      </p>
      {!isRecruitment && (
        <>
          <p className="text-body-medium">
            <span className="font-medium">Registration Open Date:</span>{" "}
            {toDateFormat(registrationOpenDate, { format: "LLL dd, yyyy" })}
          </p>
          <p className="text-body-medium">
            <span className="font-medium">Registration Close Date:</span>{" "}
            {toDateFormat(registrationCloseDate, { format: "LLL dd, yyyy" })}
          </p>
        </>
      )}
    </div>
  );

  const submitText = () => {
    return isRecruitment ? "Submit Event" : "Publish Event";
  };

  const isFormValid = eventSchema.safeParse(form.getValues()).success;
  const isButtonDisabled = isSavingAsDraft || isSubmitting || !isFormValid;

  // Save as Draft button - show when creating a new event or editing a draft
  const saveAsDraftButton = (!isEditing || initialValues?.draft) && (
    <Button key="save-as-draft" variant="outlined" onClick={handleShowSaveAsDraftDialog} disabled={isButtonDisabled}>
      Save as Draft
    </Button>
  );

  const moreActions = saveAsDraftButton ? [saveAsDraftButton] : [];

  const submitDialogActions = ({
    onCancel,
    onSubmit,
    isSubmitting: dialogSubmitting,
    confirmLabel
  }: {
    onCancel: () => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    confirmLabel: string;
  }) => (
    <EmailPreviewModalActions
      onPreview={handlePreviewSubmitEmail}
      previewDisabled={dialogSubmitting || (showSessionUpdatePreview && !sessionPreviewInput)}
      showPreview={showSubmitEmailPreview}
    >
      <Button variant="text" onClick={onCancel} disabled={dialogSubmitting}>
        Cancel
      </Button>
      <Button
        variant="filled"
        onClick={onSubmit}
        testId="publish-button"
        loading={dialogSubmitting}
        loadingLabel={submitText()}
      >
        {confirmLabel}
      </Button>
    </EmailPreviewModalActions>
  );

  const saveAsDraftDialogContent = (
    <div className="flex flex-col">
      <p className="text-body-medium pb-6">
        Are you sure you want to save this event as a draft? It will not be visible to potential registrants until you
        publish it.
      </p>
      <p className="text-body-medium">
        <span className="font-medium">Start Date:</span>{" "}
        {toSessionDateTime(earliestSession?.date?.startDate, earliestSession?.startTime, timezone)}
      </p>
      {!isRecruitment && (
        <>
          <p className="text-body-medium">
            <span className="font-medium">Registration Open Date:</span>{" "}
            {toDateFormat(registrationOpenDate, { format: "LLL dd, yyyy" })}
          </p>
          <p className="text-body-medium">
            <span className="font-medium">Registration Close Date:</span>{" "}
            {toDateFormat(registrationCloseDate, { format: "LLL dd, yyyy" })}
          </p>
        </>
      )}
    </div>
  );

  return (
    <EventDefaultsProvider>
      <StepVisibilityProvider value={stepVisibility} onChange={setStepVisibility}>
        <div data-testid="multi-step-form">
          <MultiStepForm
            form={form}
            isEditing={isEditing}
            modelName="Event"
            customHeadline={submitText()}
            submitText={submitText()}
            backLink={{ to: (backLinkTo || basePath) as Path, title: "Cancel" }}
            steps={steps}
            renderStepper={true}
            validationSchema={eventSchema}
            onSubmit={handlePublish}
            submitDialogContent={submitDialogContent}
            submitDialogActions={showSubmitEmailPreview ? submitDialogActions : undefined}
            onSubmitDialogClosed={showSubmitEmailPreview ? resetSubmitPreview : undefined}
            submitDialogClassName={
              showSubmitEmailPreview ? `${EMAIL_PREVIEW_MODAL_CLASSNAME} max-w-[560px] p-6 pr-2` : undefined
            }
            submitDialogDisplayOverflow={showSessionUpdatePreview && sessionPreviewCandidates.length > 1}
            submitDialogHasFormElements={showSessionUpdatePreview && sessionPreviewCandidates.length > 1}
            isSubmitting={isSubmitting}
            showActions={true}
            disabledSubmit={!isFormValid}
            moreActions={moreActions}
          />

          <EmailPreviewDialog
            open={previewOpen}
            onClosed={resetSubmitPreview}
            subject={activePreview?.subject}
            html={activePreview?.html}
            loading={activePreviewLoading}
            errorMessage={activePreviewError?.message}
            headline="Email Preview"
          />

          {showSaveAsDraftDialog && (
            <Dialog
              open={showSaveAsDraftDialog}
              onClosed={() => setShowSaveAsDraftDialog(false)}
              headline="Save as Draft"
              className="sm:max-w-[423px] p-6 pr-2"
              actions={
                <>
                  <Button variant="text" onClick={() => setShowSaveAsDraftDialog(false)} disabled={isSavingAsDraft}>
                    Cancel
                  </Button>
                  <Button
                    variant="filled"
                    onClick={handleConfirmSaveAsDraft}
                    testId="save-draft-button"
                    disabled={isSavingAsDraft}
                    icon={isSavingAsDraft ? "progress_activity" : undefined}
                    iconClassName={classNames(isSavingAsDraft && "animate-spin w-6 h-6 !block")}
                  >
                    Save as Draft
                  </Button>
                </>
              }
            >
              {saveAsDraftDialogContent}
            </Dialog>
          )}
        </div>
      </StepVisibilityProvider>
    </EventDefaultsProvider>
  );
}
